import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import Env from '@ridango/core/env';
import { ModalController } from '@ionic/angular';
import { AddCompanyModalComponent } from './add-company-modal/add-company-modal.component';
import { RemoveCreditCardModalComponent } from './remove-credit-card-modal/remove-credit-card-modal.component';
import { CorporateEmployeesService } from '@ridango/components/lib/corporate-employees/corporate-employees.service';
import { Organization } from '@ridango/components/lib/corporate-employees/corporate-employees.models';
import { NotificationFacade, PaymentMethodsService } from '@ridango/components';
import { catchError, takeUntil, take } from 'rxjs/operators';
import { AuthService, BaseComponent } from '@ridango/core';
import {
    CartPaymentCard,
    CartPaymentMethod,
    CartPaymentMethodsDetails
} from '@ridango/components/lib/purchase/purchase.model';
import { of } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';

interface CreditCard {
    id: string;
    type: 'mastercard' | 'visa';
    lastFourDigits: string;
    expiryDate: string;
    logoPath: string;
}
@Component({
    templateUrl: './payment-settings-page.component.html'
})
export class PaymentSettingsPageComponent extends BaseComponent implements OnInit, OnDestroy {
    public organizations: Organization[] = [];
    public availableCountryCallingCodes: string[] = [];
    public creditCards: CreditCard[] = [];
    public paymentMethods: CartPaymentMethod[] = [];
    public savedCards: CartPaymentCard[] = [];
    public netaxeptPaymentMethod: CartPaymentMethod | null = null;
    private isLoadingPaymentData = false;

    constructor(
        @Inject('ENV') public env: Env,
        private router: Router,
        private route: ActivatedRoute,
        private translate: TranslateService,
        private modalCtrl: ModalController,
        private corporateEmployeesService: CorporateEmployeesService,
        private notification: NotificationFacade,
        private paymentMethodsService: PaymentMethodsService,
        private authService: AuthService
    ) {
        super();
    }

    ngOnInit(): void {
        this.corporateEmployeesService
            .getCountryCallingCodes()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((codes) => {
                this.availableCountryCallingCodes = codes;
            });

        if (this.env.IS_CORPORATE && this.authService.getLoginStatus()) {
            this.corporateEmployeesService
                .getEmployeeOrganizations()
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((organizations) => {
                    this.organizations = organizations;
                });
        }

        this.loadPaymentData();
        this.checkForCardAddition();
        this.setupRouterListener();
    }

    private loadPaymentData(previousCardIds?: string[]): void {
        this.creditCards = [];

        this.paymentMethodsService
            .getPaymentMethods()
            .pipe(
                take(1),
                catchError((error) => {
                    console.error(error);
                    this.isLoadingPaymentData = false;
                    return of({
                        methods: [],
                        cards: [],
                        defaultPosPaymentMethodId: '',
                        defaultPaymentCardId: ''
                    } as CartPaymentMethodsDetails);
                })
            )
            .subscribe((response) => {
                this.paymentMethods = response.methods;
                this.savedCards = response.cards;

                this.netaxeptPaymentMethod =
                    response.methods.find((method) => method.merchantType === 'NETAXEPT') || null;

                this.combineCardData();

                if (previousCardIds) {
                    const currentCardIds = response.cards.map((card) => card.id);
                    const hasNewCards = currentCardIds.some((id) => !previousCardIds.includes(id));

                    if (hasNewCards) {
                        this.notification.info(this.translate.instant('NEW_CREDIT_CARD_HAS_BEEN_ADDED'));
                    }
                }

                this.isLoadingPaymentData = false;
            });
    }

    private checkForCardAddition(): void {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('refresh') === 'true') {
            const url = new URL(window.location.href);
            url.searchParams.delete('refresh');
            window.history.replaceState({}, document.title, url.pathname);
        }
    }

    private combineCardData(): void {
        this.creditCards = this.savedCards.map((card) => ({
            id: card.id,
            type: card.type.toLowerCase() as 'mastercard' | 'visa',
            lastFourDigits: card.lastFourDigits,
            expiryDate: `${card.expirationMonth.toString().padStart(2, '0')}/${card.expirationYear.toString().slice(-2)}`,
            logoPath: this.getCardLogoPath(card.type)
        }));
    }

    private getCardLogoPath(cardType: string): string {
        switch (cardType.toUpperCase()) {
            case 'VISA':
                return 'assets/img/menu/visa.svg';
            case 'MASTERCARD':
                return 'assets/img/menu/mastercard.svg';
            default:
                return 'assets/img/menu/visa.svg';
        }
    }

    handleBackClicked(): void {
        if (window.history.length > 1) {
            window.history.back();
        } else {
            void this.router.navigate([this.translate.currentLang]);
        }
    }

    async handleAddCompanyClicked() {
        if (!this.availableCountryCallingCodes?.length) {
            return;
        }

        const modal = await this.modalCtrl.create({
            component: AddCompanyModalComponent,
            cssClass: 'add-company-modal',
            componentProps: {
                availableCountryCallingCodes: this.availableCountryCallingCodes
            }
        });

        await modal.present();

        modal.onDidDismiss().then(async ({ data }) => {
            if (data && this.env.IS_CORPORATE && this.authService.getLoginStatus()) {
                this.corporateEmployeesService
                    .getEmployeeOrganizations()
                    .pipe(takeUntil(this.componentDestroyed$))
                    .subscribe((organizations) => {
                        this.organizations = organizations;
                    });
            }
        });
    }

    async handleDeleteCreditCard(cardId: string) {
        const cardToDelete = this.creditCards.find((card) => card.id === cardId);
        if (!cardToDelete) return;

        const modal = await this.modalCtrl.create({
            component: RemoveCreditCardModalComponent,
            cssClass: 'remove-credit-card-modal',
            componentProps: {
                card: cardToDelete
            }
        });

        await modal.present();

        modal.onDidDismiss().then(({ data }) => {
            if (data?.confirmed) {
                this.paymentMethodsService
                    .deleteCard(cardId)
                    .pipe(
                        takeUntil(this.componentDestroyed$),
                        catchError((error) => {
                            console.error(error);
                            this.notification.error(this.translate.instant('FAILED_TO_REMOVE_CREDIT_CARD'));
                            return of(null);
                        })
                    )
                    .subscribe(() => {
                        this.creditCards = this.creditCards.filter((card) => card.id !== cardId);
                        this.notification.info(this.translate.instant('CREDIT_CARD_HAS_BEEN_REMOVED'));
                    });
            }
        });
    }

    handleAddNewCreditCard() {
        if (!this.netaxeptPaymentMethod) {
            this.notification.error(
                this.translate.instant('PAYMENT_METHOD_UNAVAILABLE', { paymentMethod: 'Netaxept' })
            );
            return;
        }

        let baseUrl: string;
        if (Capacitor.isNativePlatform() && this.env.PAYMENT_RETURN_URL) {
            baseUrl = this.env.PAYMENT_RETURN_URL.replace(/\/$/, '');
        } else {
            baseUrl = window.location.origin;
        }

        const redirectUrl = `${baseUrl}/${this.translate.currentLang}/payment-settings?refresh=true`;

        const request = {
            posPaymentMethodId: this.netaxeptPaymentMethod.posPaymentMethodId,
            redirectUrl: redirectUrl,
            languageCode: this.translate.currentLang
        };

        this.paymentMethodsService
            .addCard(request)
            .pipe(
                takeUntil(this.componentDestroyed$),
                catchError((error) => {
                    console.error(error);
                    this.notification.error(this.translate.instant('FAILED_TO_START_ADDING_CARD'));
                    return of(null);
                })
            )
            .subscribe((response) => {
                if (response?.addCardUrl) {
                    window.location.href = response.addCardUrl;
                }
            });
    }

    private setupRouterListener(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(() => {
                this.checkForRefreshParameter();
            });
        this.checkForRefreshParameter();
    }

    private checkForRefreshParameter(): void {
        this.route.queryParams.pipe(take(1)).subscribe((params) => {
            if (params['refresh'] === 'true') {
                this.router.navigate([], {
                    relativeTo: this.route,
                    queryParams: { refresh: null },
                    queryParamsHandling: 'merge',
                    replaceUrl: true
                });

                const previousCardIds = this.savedCards.map((card) => card.id);
                this.loadPaymentData(previousCardIds);
            }
        });
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
